import axios from 'axios'
import { type ReactNode } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { parseError } from '@/lib/errors'
import { useCurrentUser } from '@/models/user'

interface GoogleOauthProps {
  /** Shown only once the current user has connected a Google mailbox. */
  children: ReactNode
  /** Label for the connect button shown until Google is connected. */
  connectLabel?: string
}

/**
 * Gate that reveals its children only when the current user has connected a
 * Google mailbox. Until then it renders a "Connect Google" button that starts
 * the OAuth flow. Wrap anything that requires Gmail — the signature editor, the
 * send-email composer, etc.
 */
export function GoogleOauth({ children, connectLabel = 'Connect Google' }: GoogleOauthProps) {
  const { user, isLoading } = useCurrentUser({ enabled: true })

  if (isLoading) return null
  if (user?.googleMailConnected) return <>{children}</>

  async function startOauth() {
    try {
      const returnTo = window.location.pathname + window.location.search
      const { data } = await axios.get<{ authorization_url: string }>('/api/v1/insure/google/oauth/start', {
        params: { return_to: returnTo },
      })
      window.location.href = data.authorization_url
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  return (
    <Button className="text-md" onClick={startOauth}>
      {connectLabel}
    </Button>
  )
}
